import { combine, createEffect, createEvent, createStore, sample } from 'effector';
import { errorHandlerFx } from '../../../model/errorHandler';
import { createEffectJWT } from '../../../model/JWT';
import {
  DynamicObjectExportTask,
  dynamicObjectExportTaskCreate,
  dynamicObjectExportTaskGetList,
  DynamicObjectExportTaskGetListParams,
} from '../../../apiRPC/dynamicObjectExportTask';
import {
  cardGetList,
  Filter as CardFilter,
  RCardGet,
} from '../../../apiRPC/card';
import {
  columnGetList,
  Filter as ColumnFilter,
} from '../../../apiRPC/column';
import { $ListBoardForPage, getListBoardForPageFx } from '../../model/board';
import { ADDITIONAL_FIELDS_META } from '../desk/modals/TaskOverview/AdditionalFields/model';

export interface DynamicObjectExportBoardOption {
  uuid: string;
  title: string;
}

export const $boards = $ListBoardForPage.map<DynamicObjectExportBoardOption[]>(
  (payload) =>
    payload?.rows.map((board) => ({
      uuid: board.uuid,
      title: board.title,
    })) ?? [],
);

export const setSelectedBoardIds = createEvent<string[]>();
export const resetSelectedBoardIds = createEvent();

export const $selectedBoardIds = createStore<string[]>([])
  .on(setSelectedBoardIds, (_, ids) => ids)
  .reset([resetSelectedBoardIds, getListBoardForPageFx]);

export interface CardsWithDynamicObjectsState {
  total: number;
  rows: RCardGet[];
}

function stableBoardIdsKey(boardIds: string[]): string {
  return [...boardIds].sort().join('\0');
}

type FetchCardsApplyResult = { stale: false; data: CardsWithDynamicObjectsState } | { stale: true };

export const fetchCardsFx = errorHandlerFx(
  createEffectJWT(async (boardIds: string[]): Promise<FetchCardsApplyResult> => {
    const key = stableBoardIdsKey(boardIds);
    const responses = await Promise.all(
      boardIds.map((board) =>
        cardGetList({
          filter: {
            board,
            hasDynamicObjects: true,
          } as CardFilter,
        }),
      ),
    );
    if (stableBoardIdsKey($selectedBoardIds.getState()) !== key) {
      return { stale: true };
    }
    const rows = responses.flatMap((r) => r.rows);
    const order = new Map(boardIds.map((id, i) => [id, i]));
    rows.sort((a, b) => {
      const ai = order.get(a.board) ?? 0;
      const bi = order.get(b.board) ?? 0;
      if (ai !== bi) return ai - bi;
      return a.title.localeCompare(b.title);
    });
    return {
      stale: false,
      data: { total: rows.length, rows },
    };
  }),
);

type ColumnsById = Record<string, string>;

type FetchColumnsApplyResult = { stale: false; data: ColumnsById } | { stale: true };

export const fetchColumnsFx = errorHandlerFx(
  createEffectJWT(async (boardIds: string[]): Promise<FetchColumnsApplyResult> => {
    const key = stableBoardIdsKey(boardIds);
    const responses = await Promise.all(
      boardIds.map((board) =>
        columnGetList({
          filter: { board, status: 'ACTIVE' } satisfies ColumnFilter,
        }),
      ),
    );
    if (stableBoardIdsKey($selectedBoardIds.getState()) !== key) {
      return { stale: true };
    }
    const next: ColumnsById = {};
    for (const payload of responses) {
      for (const column of payload.rows) {
        if (column.status === 'ACTIVE') {
          next[column.uuid] = column.title;
        }
      }
    }
    return { stale: false, data: next };
  }),
);

export const resetCards = createEvent();

export const $cards = createStore<CardsWithDynamicObjectsState | null>(null)
  .on(fetchCardsFx.doneData, (state, result) => (result.stale ? state : result.data))
  .reset([setSelectedBoardIds, resetCards, getListBoardForPageFx]);

export const $columnsById = createStore<ColumnsById>({})
  .on(fetchColumnsFx.doneData, (state, result) => (result.stale ? state : result.data))
  .reset([setSelectedBoardIds, getListBoardForPageFx]);

export const resetSelectedCardIds = createEvent();
export const $selectedCardIds = createStore<Set<string>>(new Set());
export const toggleCard = createEvent<string>();
export const selectAllCards = createEvent();
export const clearSelectedCards = createEvent();

$selectedCardIds
  .on(toggleCard, (state, cardId) => {
    const next = new Set(state);
    if (next.has(cardId)) {
      next.delete(cardId);
    } else {
      next.add(cardId);
    }
    return next;
  })
  .on(selectAllCards, (state) => {
    const cards = $cards.getState();
    if (!cards) return state;
    const next = new Set<string>();
    cards.rows.forEach((card) => {
      next.add(card.uuid);
    });
    return next;
  })
  .on(clearSelectedCards, () => new Set<string>())
  .reset([setSelectedBoardIds, resetSelectedCardIds, getListBoardForPageFx]);

export const $isAllSelected = combine(
  $cards,
  $selectedCardIds,
  (cards, selectedIds) => {
    if (!cards || cards.rows.length === 0) {
      return false;
    }
    return cards.rows.every((card) => selectedIds.has(card.uuid));
  },
);

export const fetchExportTasksFx = errorHandlerFx(
  createEffectJWT((params: DynamicObjectExportTaskGetListParams) =>
    dynamicObjectExportTaskGetList(params),
  ),
);

export const DEFAULT_EXPORT_TASKS_PAGE_SIZE = 20;
export const EXPORT_TASKS_PAGE_SIZE_OPTIONS: readonly number[] = [10, 20, 50];

export const setExportTasksPage = createEvent<number>();
export const setExportTasksPageSize = createEvent<number>();
export const loadExportTasksPage = createEvent();
export const resetExportTasksPage = createEvent();

export const $exportTasksPage = createStore<number>(0)
  .on(setExportTasksPage, (_, page) => page)
  .on(setExportTasksPageSize, () => 0)
  .reset(resetExportTasksPage);

export const $exportTasksPageSize = createStore<number>(DEFAULT_EXPORT_TASKS_PAGE_SIZE).on(
  setExportTasksPageSize,
  (_, size) => size,
);

export const resetLastExportTasksParams = createEvent();
export const resetExportTasks = createEvent();
export const resetExportTasksTotal = createEvent();

export const $lastExportTasksParams = createStore<DynamicObjectExportTaskGetListParams>({})
  .on(fetchExportTasksFx, (_, params) => params ?? {})
  .on(fetchExportTasksFx.done, (state, { params }) => params ?? state)
  .reset(resetLastExportTasksParams);

export const $exportTasks = createStore<DynamicObjectExportTask[]>([])
  .on(fetchExportTasksFx.doneData, (_, payload) => payload.list)
  .reset(resetExportTasks);

export const $exportTasksTotal = createStore<number>(0)
  .on(fetchExportTasksFx.doneData, (_, payload) => payload.total)
  .reset(resetExportTasksTotal);

export const tickExportTasks = createEvent();

function buildExportTasksParams(page: number, pageSize: number): DynamicObjectExportTaskGetListParams {
  return {
    filter: { limit: pageSize, offset: page * pageSize },
  };
}

sample({
  clock: [loadExportTasksPage, tickExportTasks],
  source: { page: $exportTasksPage, pageSize: $exportTasksPageSize },
  fn: ({ page, pageSize }) => buildExportTasksParams(page, pageSize),
  target: fetchExportTasksFx,
});

sample({
  clock: setExportTasksPage,
  source: $exportTasksPageSize,
  fn: (pageSize, page) => buildExportTasksParams(page, pageSize),
  target: fetchExportTasksFx,
});

sample({
  clock: setExportTasksPageSize,
  fn: (pageSize) => buildExportTasksParams(0, pageSize),
  target: fetchExportTasksFx,
});

export const startExportTasksAutoUpdate = createEvent();
export const stopExportTasksAutoUpdate = createEvent();

let exportTasksIntervalId: number | null = null;

const startExportTasksAutoUpdateFx = createEffect(() => {
  if (exportTasksIntervalId !== null) {
    window.clearInterval(exportTasksIntervalId);
  }

  exportTasksIntervalId = window.setInterval(() => {
    tickExportTasks();
  }, 30_000);
});

const stopExportTasksAutoUpdateFx = createEffect(() => {
  if (exportTasksIntervalId !== null) {
    window.clearInterval(exportTasksIntervalId);
    exportTasksIntervalId = null;
  }
});

sample({
  clock: startExportTasksAutoUpdate,
  target: startExportTasksAutoUpdateFx,
});

sample({
  clock: stopExportTasksAutoUpdate,
  target: stopExportTasksAutoUpdateFx,
});

export interface CreateExportTaskParams {
  name?: string;
}

export const createExportTaskFx = errorHandlerFx(
  createEffectJWT(async ({ name }: CreateExportTaskParams) => {
    const boardIds = $selectedBoardIds.getState();
    const cards = $cards.getState();
    const selectedIds = $selectedCardIds.getState();

    if (boardIds.length === 0) {
      throw new Error('Не выбраны доски');
    }

    if (!cards || cards.rows.length === 0) {
      throw new Error('Нет карточек с дополнительными полями');
    }

    const hasExplicitCards = selectedIds.size > 0;

    const properties = Object.keys(ADDITIONAL_FIELDS_META);

    const payload = {
      fields: {
        name,
        properties,
        ...(hasExplicitCards
          ? { cards: Array.from(selectedIds) }
          : { boards: boardIds }),
      },
    };

    return dynamicObjectExportTaskCreate(payload);
  }),
);

export const resetCreatePending = createEvent();
export const resetListPending = createEvent();
export const resetCardsPending = createEvent();
export const resetError = createEvent();

export const $createPending = createStore(false)
  .on(createExportTaskFx.pending, (_, pending) => pending)
  .reset([setSelectedBoardIds, resetCreatePending]);

export const $listPending = createStore(false)
  .on(fetchExportTasksFx.pending, (_, pending) => pending)
  .reset([setSelectedBoardIds, resetListPending]);

export const $cardsPending = createStore(false)
  .on(fetchCardsFx.pending, (_, pending) => pending)
  .reset([setSelectedBoardIds, resetCardsPending]);

export const $error = createStore<string | null>(null)
  .on(
    [
      createExportTaskFx.failData,
      fetchExportTasksFx.failData,
      fetchCardsFx.failData,
      fetchColumnsFx.failData,
    ],
    () => 'Ошибка при работе с отчётом по обороту и выручке',
  )
  .reset([
    createExportTaskFx.done,
    fetchExportTasksFx.done,
    fetchCardsFx.done,
    fetchColumnsFx.done,
    resetError,
  ]);

sample({
  clock: setSelectedBoardIds,
  filter: (ids) => ids.length > 0,
  fn: (ids) => ids,
  target: fetchCardsFx,
});

sample({
  clock: setSelectedBoardIds,
  filter: (ids) => ids.length > 0,
  fn: (ids) => ids,
  target: fetchColumnsFx,
});

sample({
  clock: createExportTaskFx.done,
  fn: () => 0,
  target: setExportTasksPage,
});

export function resetAllStates() {
  resetSelectedBoardIds();
  resetCards();
  resetSelectedCardIds();
  resetLastExportTasksParams();
  resetExportTasks();
  resetExportTasksTotal();
  resetExportTasksPage();
  resetCreatePending();
  resetListPending();
  resetCardsPending();
  resetError();
}

